import { AttachFile, Close, Send } from "@mui/icons-material"
import { Dialog, DialogContent, DialogTitle, IconButton as MuiIconButton, Link } from "@mui/material"
import { ChangeEvent, forwardRef, KeyboardEvent, useCallback, useRef, useState } from "react"
import { intl } from "src/internationalization/intl"
import { Button, Grid, IconButton, Typography, CircularProgress } from "src/lib"
import { TextInput } from "src/lib/components/basics/TextInput"
import { getMarketRankingDisplayName } from "src/parts/CommitmentsSection/useRankingAssets"
import { getResponseFromQuestion, getResponseFromQuestionWithContext } from "src/services/api/chat"
import {
    ATTACHMENT_FILE_INPUT_ACCEPT,
    MAX_ATTACHMENTS_PER_MESSAGE,
    fileToPendingAttachment,
    validateAttachmentFile,
} from "src/services/api/chat/attachments"
import { setSortingProducts, type SetSortingProductsPayload } from "src/services/api/sorting"
import { PendingAttachment } from "src/types/chat"
import { useConfirmedRankingStore } from "src/store/confirmedRankingStore"
import { useContextDocumentsStore } from "src/store/contextDocumentsStore"
import { useMessageStore } from "src/store/messageStore"
import { useRankingStore } from "src/store/rankingStore"
import { AttachmentPreview } from "./AttachmentPreview"
import { ContextLabel } from "../ContextLabel"
import { RankingPropertiesPicker } from "./RankingPropertiesPicker"
import { StyledBottom, StyledComposer, StyledContextSection } from "./styled"
import { StyledGlassPanel } from "../GlassPanel"
import { useBatchedStreamCallbacks } from "src/hooks/useBatchedStreamCallbacks"
import { IChatBottomProps } from "./types"

const BottomArea = forwardRef<HTMLDivElement, IChatBottomProps>((props, ref) => {
    const addContextDocuments = useContextDocumentsStore((s: any) => s.addDocuments)
    const setActualContext = useMessageStore((s: any) => s.setActualContext)
    const setJsonContext = useMessageStore((s: any) => s.setJsonContext)
    const actualContext = useMessageStore((s: any) => s.actualContext)
    const jsonContext = useMessageStore((s: any) => s.jsonContext)
    const addMessage = useMessageStore((s: any) => s.addMessage)
    const updateMessage = useMessageStore((s: any) => s.updateMessage)
    const isStreaming = useMessageStore((s: any) => s.isStreaming)
    const setIsStreaming = useMessageStore((s: any) => s.setIsStreaming)
    const { createCallbacks, flushPending } = useBatchedStreamCallbacks()

    const rankings = useRankingStore((s: any) => s.rankings)
    const asset = useRankingStore((s: any) => s.asset)
    const assetKey = useRankingStore((s: any) => s.assetKey)
    const sortingStep = useRankingStore((s: any) => s.sortingStep)
    const sortingContext = useRankingStore((s: any) => s.sortingContext)
    const clearRankings = useRankingStore((s: any) => s.clearRankings)
    const setSortingResponse = useRankingStore((s: any) => s.setSortingResponse)
    const setConfirmedRanking = useConfirmedRankingStore((s: any) => s.setConfirmedRanking)

    const [text, setText] = useState("")
    const [propertiesModalOpen, setPropertiesModalOpen] = useState(false)
    const [propertiesPanelOpen, setPropertiesPanelOpen] = useState(false)
    const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([])
    const [attachmentError, setAttachmentError] = useState<string | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const isRankingEditMode = !!rankings
    const marketKey = assetKey ?? asset ?? ""
    const awaitingSortingConfirm = isRankingEditMode && sortingStep === 1 && !!sortingContext

    const changeNewMessage = (value: string) => {
        setText(value)
    }

    const handleAttachClick = () => {
        if (isAttachDisabled) return
        fileInputRef.current?.click()
    }

    const handleRemoveAttachment = (id: string) => {
        setPendingAttachments((prev) => prev.filter((a) => a.id !== id))
    }

    const handleFilesSelected = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? [])
        e.target.value = ""
        if (!files.length) return

        setAttachmentError(null)
        const availableSlots = MAX_ATTACHMENTS_PER_MESSAGE - pendingAttachments.length
        const accepted: File[] = []
        for (const file of files) {
            if (accepted.length >= availableSlots) {
                setAttachmentError(`No máximo ${MAX_ATTACHMENTS_PER_MESSAGE} arquivos por mensagem.`)
                break
            }
            const error = validateAttachmentFile(file)
            if (error) {
                setAttachmentError(error)
                continue
            }
            accepted.push(file)
        }
        if (!accepted.length) return

        const converted = await Promise.all(accepted.map(fileToPendingAttachment))
        setPendingAttachments((prev) => [...prev, ...converted])
    }

    const insertPromptSnippet = useCallback((snippet: string) => {
        const s = snippet.trim()
        if (!s) return
        setText((prev) => {
            const trimmed = prev.trim()
            return trimmed ? `${trimmed}; ${s}` : s
        })
    }, [])

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey && !!text.trim() && !isStreaming) {
            e.preventDefault()
            handleClickSend()
        }
    }

    const streamCallbacks = (assistantId: string) => createCallbacks(assistantId)

    const finalizeAssistantMessage = (
        assistantId: string,
        patch: Parameters<typeof updateMessage>[1]
    ) => {
        flushPending(assistantId)
        updateMessage(assistantId, patch)
    }

    const applySortingResponse = (market: string, response: Awaited<ReturnType<typeof setSortingProducts>>) => {
        const nextStep = response.next_step
        if (response.next_step === 3 && response.ranking_text) {
            setConfirmedRanking(market, {
                ranking_text: response.ranking_text,
                cypher_query: response.cypher_query,
            })
        }
        if (nextStep === 1) {
            setSortingResponse(1, {
                ranking_text: response.ranking_text,
                cypher_query: response.cypher_query,
            })
        } else if (nextStep === 2 && response.clarification) {
            setSortingResponse(
                2,
                { ranking_text: response.ranking_text },
                { round: response.clarification.round, questions: response.clarification.questions }
            )
        } else {
            setSortingResponse(0)
        }
    }

    const runSortingRequest = async (
        market: string,
        payload: SetSortingProductsPayload,
        userVisibleText: string
    ) => {
        const context = actualContext
        addMessage({ text: userVisibleText, context, jsonContext, answer: false })
        setIsStreaming(true)

        const assistantId = addMessage({
            text: "",
            context,
            jsonContext,
            answer: true,
            status: "streaming",
            streamStatus: intl("chatStreamingStarting"),
            questionText: userVisibleText,
        })

        try {
            const response = await setSortingProducts(payload, streamCallbacks(assistantId))
            applySortingResponse(market, response)
            finalizeAssistantMessage(assistantId, {
                text: response.natural_language_response ?? "Ordenação enviada com sucesso.",
                status: "done",
                streamStatus: undefined,
                jsonMessage: response,
                questionText: userVisibleText,
            })
        } catch (error) {
            const msg = error instanceof Error ? error.message : String(error)
            const forbidden = msg.includes("403")
            const errorText = forbidden
                ? intl("rankingEditForbidden")
                : msg || intl("chatMessageSendError")
            flushPending(assistantId)
            const partial =
                useMessageStore.getState().messages.find((m: any) => m.id === assistantId)?.text || ""
            updateMessage(assistantId, {
                text: partial ? `${partial}\n\n${errorText}` : errorText,
                status: "error",
                streamStatus: undefined,
                questionText: userVisibleText,
            })
        } finally {
            setIsStreaming(false)
        }
    }

    const handleSortingConfirmCancel = async (action: "confirm" | "cancel") => {
        if (!awaitingSortingConfirm || isStreaming) return
        const market = assetKey ?? asset
        if (!market || !sortingContext) return

        const userVisibleText =
            action === "confirm" ? intl("rankingConfirmUserMessage") : intl("rankingCancelUserMessage")

        await runSortingRequest(
            market,
            {
                market,
                step: 1,
                action,
                ranking_text: sortingContext.ranking_text,
                cypher_query: sortingContext.cypher_query,
            },
            userVisibleText
        )
    }

    const handleClickSend = async () => {
        if (!text.trim() || isStreaming) return
        const currentText = text
        const context = actualContext
        const jsonContextData = jsonContext
        const attachments = pendingAttachments.map((a) => a.payload)
        const attachmentsForDisplay = pendingAttachments.map((a) => ({
            filename: a.filename,
            mime_type: a.payload.mime_type,
            sizeBytes: a.sizeBytes,
        }))
        const contextDocuments = pendingAttachments.map((a) => ({ filename: a.filename, mimeType: a.payload.mime_type }))

        setText("")
        setPendingAttachments([])
        setAttachmentError(null)
        if (contextDocuments.length) {
            addContextDocuments(contextDocuments)
        }

        if (isRankingEditMode) {
            const market = assetKey ?? asset
            if (!market) return

            // Text always regenerates the suggestion (step 0). Confirm/cancel use the buttons.
            await runSortingRequest(
                market,
                { market, step: 0, ranking_text: currentText.trim() },
                currentText
            )
            return
        }

        addMessage({
            text: currentText,
            context,
            jsonContext,
            answer: false,
            attachments: attachmentsForDisplay.length ? attachmentsForDisplay : undefined,
        })
        setIsStreaming(true)

        const assistantId = addMessage({
            text: "",
            context,
            jsonContext,
            answer: true,
            status: "streaming",
            streamStatus: intl("chatStreamingStarting"),
            questionText: currentText,
        })

        try {
            if (!context) {
                const responseText = await getResponseFromQuestion(
                    { question: currentText, attachments: attachments.length ? attachments : undefined },
                    streamCallbacks(assistantId)
                )
                finalizeAssistantMessage(assistantId, {
                    text: responseText.natural_language_response,
                    status: "done",
                    streamStatus: undefined,
                    jsonMessage: responseText,
                    questionText: currentText,
                })
            } else {
                const responseText = await getResponseFromQuestionWithContext(
                    {
                        question: currentText,
                        context: jsonContextData || {},
                        attachments: attachments.length ? attachments : undefined,
                    },
                    streamCallbacks(assistantId)
                )
                finalizeAssistantMessage(assistantId, {
                    text: responseText.natural_language_response,
                    jsonContext,
                    context,
                    status: "done",
                    streamStatus: undefined,
                    jsonMessage: responseText,
                    questionText: currentText,
                })
            }
        } catch (error) {
            const msg = error instanceof Error ? error.message : String(error)
            const errorText = msg || intl("chatMessageSendError")
            flushPending(assistantId)
            const partial =
                useMessageStore.getState().messages.find((m: any) => m.id === assistantId)?.text || ""
            updateMessage(assistantId, {
                text: partial ? `${partial}\n\n${errorText}` : errorText,
                status: "error",
                streamStatus: undefined,
                questionText: currentText,
            })
        } finally {
            setIsStreaming(false)
        }
    }

    const handleCloseContextCard = () => {
        setActualContext(undefined)
        setJsonContext(undefined)
        clearRankings()
        setPropertiesModalOpen(false)
        setPropertiesPanelOpen(false)
    }

    const marketDisplayName = marketKey ? getMarketRankingDisplayName(marketKey) : ""

    const isAttachDisabled = isStreaming || pendingAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE
    const isSendDisabled = !text.trim()

    return (
        <StyledBottom ref={ref}>
            <Grid sx={{ width: "100%", maxWidth: 640 }}>
                {!isRankingEditMode && (pendingAttachments.length > 0 || attachmentError) && (
                    <Grid container direction="column" gap={0.5} sx={{ mb: 1, px: 0.5 }}>
                        {pendingAttachments.length > 0 && (
                            // Wraps onto more rows instead of overflowing — same behavior as an
                            // already-sent message's attachment row (.chat-attachments).
                            <Grid container gap={0.5} wrap="wrap">
                                {pendingAttachments.map((attachment) => (
                                    <AttachmentPreview
                                        key={attachment.id}
                                        attachment={attachment}
                                        onRemove={handleRemoveAttachment}
                                    />
                                ))}
                            </Grid>
                        )}
                        {attachmentError && (
                            <Typography variant="caption" sx={{ color: "error.main" }}>
                                {attachmentError}
                            </Typography>
                        )}
                    </Grid>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ATTACHMENT_FILE_INPUT_ACCEPT}
                    style={{ display: "none" }}
                    onChange={handleFilesSelected}
                />
                <Dialog
                    open={propertiesModalOpen}
                    onClose={() => setPropertiesModalOpen(false)}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle>
                        <Typography variant="h4">
                            {intl("rankingPropertiesModalTitle", {
                                values: { market: marketDisplayName || marketKey },
                            })}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        {marketKey ? (
                            <RankingPropertiesPicker
                                marketKey={marketKey}
                                marketLabel={marketDisplayName}
                                variant="modal"
                                onInsertPrompt={(snippet) => {
                                    insertPromptSnippet(snippet)
                                    setPropertiesModalOpen(false)
                                }}
                            />
                        ) : (
                            <Typography variant="body2" color="secondary">
                                {intl("rankingPropertiesEmpty")}
                            </Typography>
                        )}
                    </DialogContent>
                </Dialog>
                {awaitingSortingConfirm && (
                    <Grid
                        container
                        direction="column"
                        gap={0.75}
                        sx={{
                            mb: 1.5,
                            px: 0.5,
                        }}
                    >
                        <Typography variant="caption" color="quaternary">
                            {intl("rankingConfirmHint")}
                        </Typography>
                        <Grid container gap={0.5} justifyContent="flex-end">
                            <Button
                                variant="text"
                                color="neutral"
                                size="small"
                                onClick={() => handleSortingConfirmCancel("cancel")}
                                disabled={isStreaming}
                                sx={{ fontSize: "0.8125rem", minWidth: "auto", px: 1 }}
                            >
                                {intl("rankingCancelButton")}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleSortingConfirmCancel("confirm")}
                                disabled={isStreaming}
                                sx={{ fontSize: "0.8125rem", minWidth: "auto", px: 1.5, boxShadow: "none" }}
                            >
                                {intl("rankingConfirmButton")}
                            </Button>
                        </Grid>
                    </Grid>
                )}
                <StyledComposer hasContext={!!props.context}>
                    {!!props.context && (
                        <StyledContextSection>
                            <Grid container justifyContent="space-between" alignItems="center">
                                <ContextLabel context={props.context!} />
                                <IconButton variant="clean" size="small" onClick={handleCloseContextCard}>
                                    <Close fontSize="small" />
                                </IconButton>
                            </Grid>
                            {isRankingEditMode && marketKey && (
                                <Grid sx={{ mt: 0.75 }}>
                                    <Link
                                        component="button"
                                        type="button"
                                        variant="body2"
                                        underline="hover"
                                        onClick={() => setPropertiesPanelOpen((v) => !v)}
                                        sx={{ cursor: "pointer", fontWeight: 600 }}
                                    >
                                        {propertiesPanelOpen
                                            ? intl("rankingHideProperties")
                                            : intl("rankingViewProperties")}
                                    </Link>
                                    {propertiesPanelOpen && (
                                        <Grid
                                            sx={{
                                                mt: 1,
                                                p: 1,
                                                borderRadius: 1,
                                                border: (t) => `1px solid ${t.palette.divider}`,
                                                bgcolor: "background.paper",
                                            }}
                                        >
                                            <RankingPropertiesPicker
                                                marketKey={marketKey}
                                                marketLabel={marketDisplayName}
                                                variant="inline"
                                                onInsertPrompt={insertPromptSnippet}
                                                onOpenModal={() => setPropertiesModalOpen(true)}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </StyledContextSection>
                    )}
                    <StyledGlassPanel
                        sx={
                            props.context
                                ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 }
                                : undefined
                        }
                    >
                        <Grid
                            container
                            data-testid="chat-input-area"
                            gap={1}
                            alignItems="center"
                            wrap="nowrap"
                            sx={{ width: "100%" }}
                        >
                            {!isRankingEditMode && (
                                <Grid
                                    sx={{
                                        opacity: isAttachDisabled ? 0.4 : 1,
                                        pointerEvents: isAttachDisabled ? "none" : "auto",
                                    }}
                                >
                                    <MuiIconButton
                                        size="medium"
                                        onClick={handleAttachClick}
                                        sx={{
                                            color: "primary.darker",
                                            "&:hover": { backgroundColor: "transparent" },
                                        }}
                                    >
                                        <AttachFile fontSize="medium" />
                                    </MuiIconButton>
                                </Grid>
                            )}
                            <Grid sx={{ flexGrow: 1, minWidth: 0 }}>
                                <TextInput
                                    autoComplete="off"
                                    placeholder={
                                        assetKey
                                            ? intl("rankingChatPlaceholder")
                                            : "Digite sua mensagem aqui..."
                                    }
                                    fullWidth
                                    multiline
                                    minRows={1}
                                    maxRows={6}
                                    sx={{ paddingTop: 0, paddingBottom: 0 }}
                                    value={text}
                                    onChange={(e) => {
                                        changeNewMessage(e.target.value)
                                    }}
                                    onKeyDown={handleKeyDown}
                                    disabled={isStreaming}
                                />
                            </Grid>
                            <Grid sx={{ flexShrink: 0 }}>
                                {isStreaming ? (
                                    <Grid
                                        container
                                        justifyContent="center"
                                        alignItems="center"
                                        sx={{ height: "100%" }}
                                    >
                                        <CircularProgress size={24} color="primary" />
                                    </Grid>
                                ) : (
                                    <Grid
                                        sx={{
                                            opacity: isSendDisabled ? 0.4 : 1,
                                            pointerEvents: isSendDisabled ? "none" : "auto",
                                        }}
                                    >
                                        <MuiIconButton
                                            size="medium"
                                            onClick={handleClickSend}
                                            sx={{
                                                color: "primary.darker",
                                                "&:hover": { backgroundColor: "transparent" },
                                            }}
                                        >
                                            <Send fontSize="medium" />
                                        </MuiIconButton>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </StyledGlassPanel>
                </StyledComposer>
            </Grid>
        </StyledBottom>
    )
})

BottomArea.displayName = "BottomArea"

export { BottomArea }
